import { useCallback, useState } from 'react';

import { useCreatePaymentMethod, useUserData } from '@givebacks/api/core';
import { useForm, useFormyValidator } from '@givebacks/formy';
import { getErrorMessage } from '@givebacks/utils';
import { useBreakPoints } from 'core';
import { ShopToGiveTermsCheckboxCard, StripeCardForm, StripeCardInfoForm } from 'core/Components';
import { useNavigate } from 'react-router-dom';
import { Button, ImageMantine, OneToTwoLayoutTemplate, Stack, Title, notifications } from 'ui';
import { UseFormReturnType, isNotEmpty } from 'ui/Form';

import { AddCardProvider, useAddCardProvider } from './AddCard.context';

const AddCardButton = () => {
  const { data: userData } = useUserData();
  const { setError } = useAddCardProvider();
  const { mutate: createPaymentMethod, isPending } = useCreatePaymentMethod();
  const navigate = useNavigate();

  const { useGetForm, validateForms } = useFormyValidator<{
    stripe_credit_card: StripeCardForm;
    terms: UseFormReturnType<{ terms: boolean }>;
  }>();
  const stripeCardForm = useGetForm('stripe_credit_card');
  const termsForm = useGetForm('terms');

  const handleClick = useCallback(async () => {
    if (!validateForms().isValid) {
      return;
    }
    const { token, error } = await stripeCardForm.getTransformedValues();
    if (error) {
      setError('Invalid card');
      return;
    }
    if (!stripeCardForm?.validate().hasErrors && !termsForm?.validate().hasErrors) {
      createPaymentMethod(
        {
          email: userData?.email ?? null,
          card_token: token,
          save_for_future_use: false,
          enroll_in_shop_to_give: true,
        },
        {
          onSuccess() {
            notifications.show({
              message: 'Card added successfully',
              variant: 'success',
            });
            navigate('/account');
          },
          onError(error) {
            setError(getErrorMessage(error));
          },
        }
      );
    }
  }, [
    createPaymentMethod,
    navigate,
    setError,
    stripeCardForm,
    termsForm,
    userData?.email,
    validateForms,
  ]);

  return (
    <Stack>
      <Button fullWidth onClick={() => void handleClick()} loading={isPending}>
        Add Card
      </Button>
    </Stack>
  );
};

const LeftSection = () => {
  const { isSmallScreen } = useBreakPoints();
  return (
    <Stack gap={isSmallScreen ? 'md' : 'lg'}>
      <ImageMantine
        src={'https://s3.us-east-1.amazonaws.com/com.givebacks.public/images/stripe/card.png'}
      />
      {!isSmallScreen && <AddCardButton />}
    </Stack>
  );
};

const CardInfoSection = () => {
  const form = useForm(
    { initialValues: { terms: false }, validate: { terms: isNotEmpty('Terms are required') } },
    {
      id: 'terms',
    }
  );
  return (
    <>
      <StripeCardInfoForm />

      <ShopToGiveTermsCheckboxCard
        checkboxFieldProps={form.getInputProps('terms', { type: 'checkbox' })}
        hasError={!!form.errors.terms}
      />
    </>
  );
};

const AddCard = () => {
  const [error, setError] = useState<string | undefined>();
  const { isSmallScreen } = useBreakPoints();

  return (
    <AddCardProvider value={{ error, setError }}>
      <OneToTwoLayoutTemplate
        leftSection={<LeftSection />}
        rightSection={
          <Stack>
            {!isSmallScreen && <Title order={2}>Add Card</Title>}
            <CardInfoSection />
          </Stack>
        }
        mobileBottomButton={<AddCardButton />}
      />
    </AddCardProvider>
  );
};

export { AddCard };
